import Select from 'react-select';
import Masonry from 'react-masonry-css';
import { toast } from 'react-toastify';
import { TextField, Checkbox } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Container, Row, Col, Card, CardBody, CardHeader, CardTitle, Badge, Button, Input, InputGroup, InputGroupText, Form, FormGroup, Label, Alert, UncontrolledTooltip, Modal, ModalHeader, ModalBody, ModalFooter, Table, ButtonGroup, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Collapse } from 'reactstrap';

import axios from '../../../store/axios';
import PlanEditModal from './PlanEditModal';
import { getAdminPlans, togglePlanStatus } from '../../../store/Admin';
import { color, width } from '../../../../node_modules/@mui/system/index';

const Plans = ({ isAgencyView = false, workspaceId = null, accessType }) => {
  const dispatch = useDispatch();
  const { currentWorkspace } = useSelector(state => state['Settings']);
  const { adminPlans, loading: adminPlansLoading, workspaces } = useSelector(state => ({ adminPlans: state['Admin']['adminPlans'], loading: state['Admin']['loading'], workspaces: state['Admin']['workspaces'] || [] }));

  // Gateway states
  const [formData, setFormData] = useState({ secretKey: '' });
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [gatewayStatus, setGatewayStatus] = useState(null);
  const [gatewayLoading, setGatewayLoading] = useState(true);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [isGatewayCollapsed, setIsGatewayCollapsed] = useState(false);
  const breakpoints = { default: 3, 991: 2, 700: 1 };

  // Plans states
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [viewMode, setViewMode] = useState('card');
  const [agencyPlans, setAgencyPlans] = useState([]);
  const [loadingAgencyPlans, setLoadingAgencyPlans] = useState(false);

  // Business plans for agency (dynamically generated)
  const [businessPlans, setBusinessPlans] = useState([]);
  const [loadingBusinessPlans, setLoadingBusinessPlans] = useState(false);
  const [showPriceEditModal, setShowPriceEditModal] = useState(false);
  const [selectedPlanForPriceEdit, setSelectedPlanForPriceEdit] = useState(null);

  // Agency plan assignment states removed - no longer needed

  // Admin plan assignment states (for non-agency users)
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedPlanForAssign, setSelectedPlanForAssign] = useState(null);
  const [selectedWorkspaceForAssign, setSelectedWorkspaceForAssign] = useState(null);
  const [assignmentReason, setAssignmentReason] = useState('');
  const [assignmentLoading, setAssignmentLoading] = useState(false);

  // Load gateway status and plans on mount
  useEffect(() => {
    const initializeData = async () => {
      // First fetch gateway status
      await fetchGatewayStatus();

      if (!isAgencyView) {
        loadPlans();
      }
      // For agency view, plans will load via the gatewayStatus useEffect below
      loadWorkspaces();
    };

    initializeData();
  }, [isAgencyView, workspaceId]);

  // Load business plans when gateway becomes configured
  useEffect(() => {
    if (gatewayStatus?.['isConfigured']) {
      loadBusinessPlans();
      loadAgencyPlans();
    }
  }, [gatewayStatus?.['isConfigured']]);

  // Fetch gateway status
  const fetchGatewayStatus = async () => {
    setGatewayLoading(true);
    try {
      const wsId = workspaceId || workspaceId || currentWorkspace?.['_id'] || '';
      if (!wsId) {
        setGatewayLoading(false);
        return;
      }

      const response = await axios.get(`/payment/gateway/status/${wsId}?_=${Date.now()}`);
      if (response['data']['code'] === 200) {
        const statusData = response['data']['body'];
        setGatewayStatus(statusData);
        console.log('Gateway status loaded:', statusData);
      }
    } catch (error) {
      console.error('Error fetching gateway status:', error);
      setGatewayStatus(null);
    } finally {
      setGatewayLoading(false);
    }
  };

  // Load plans based on access type
  const loadPlans = () => {
    if (!isAgencyView) dispatch(getAdminPlans());
  };

  // Load workspaces for assignment
  const loadWorkspaces = async () => {
    try {
      const response = await axios.get('/admin/users/workspaces');
      // Will be handled by redux store
    } catch (error) {
      console.error('Error loading workspaces:', error);
    }
  };

  // Validate form inputs
  const validateForm = () => {
    const newErrors = {};

    if (!formData['secretKey']) newErrors['secretKey'] = 'Secret key is required';
    else if (!/^sk_(test|live)_[a-zA-Z0-9]{24,}$/.test(formData['secretKey']))
      newErrors['secretKey'] = 'Invalid secret key format (should start with sk_test_ or sk_live_)';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setValidating(true);
    try {
      const wsId = workspaceId || workspaceId || currentWorkspace?.['_id'] || '';
      const response = await axios.post(`/payment/gateway/configure/${wsId}`, formData);

      if (response['data']['code'] === 200) {
        const { plansGenerated = 0 } = response['data']['body'] || {};

        toast.success(
          plansGenerated > 0
            ? `Payment gateway configured successfully! ${plansGenerated} business plans have been generated.`
            : 'Payment gateway configured successfully!'
        );

        // Update gateway status
        setGatewayStatus({ ...response['data']['body'], isConfigured: true });

        // Clear form
        setFormData({ secretKey: '' });

        // Force reload of plans and gateway status
        fetchGatewayStatus();

        if (isAgencyView) {
          setTimeout(() => {
            loadBusinessPlans(true);
            loadAgencyPlans();
          }, 1000);
        }
      } else {
        toast.error(response['data']['message'] || 'Failed to configure gateway');
      }
    } catch (error) {
      console.error('Configuration error:', error);
      const errorMessage = error['response']?.['data']?.['message'] || 'Failed to configure payment gateway';
      toast.error(errorMessage);

      if (error['response']?.['status'] === 400 && error['response']?.['data']?.['message']) {
        const message = error['response']['data']['message'].toLowerCase();
        if (message.includes('secret key')) setErrors({ ...errors, secretKey: error['response']['data']['message'] });
      }
    }
    setValidating(false);
  };

  // Handle validate gateway
  const handleValidate = async () => {
    setValidating(true);
    try {
      const wsId = workspaceId || workspaceId || currentWorkspace?.['_id'] || '';
      const response = await axios.post(`/payment/gateway/validate/${wsId}`);

      if (response['data']['code'] === 200 && response['data']['body']?.['isValid']) {
        const { plansGenerated = 0 } = response['data']['body'] || {};

        toast.success(
          plansGenerated > 0
            ? `Gateway validated successfully! ${plansGenerated} business plans have been generated.`
            : 'Gateway validated successfully!'
        );

        // Update validation status
        setGatewayStatus(prev => ({ ...prev, validationStatus: 'valid', lastValidated: new Date() }));

        // Reload business plans after validation
        if (isAgencyView) {
          setTimeout(() => {
            loadBusinessPlans(true);
            loadAgencyPlans();
          }, 1000);
        }
      } else {
        toast.error(response['data']['body']?.['error'] || response['data']['message'] || 'Gateway validation failed');
      }
    } catch (error) {
      console.error('Validation error:', error);
      toast.error('Failed to validate gateway');
    }
    setValidating(false);
  };

  // Handle remove gateway
  const handleRemoveGateway = async () => {
    setLoading(true);
    try {
      const wsId = workspaceId || workspaceId || currentWorkspace?.['_id'] || '';
      const response = await axios.delete(`/payment/gateway/${wsId}`);

      if (response['data']['code'] === 200) {
        toast.success('Payment gateway removed successfully');
        setGatewayStatus(null);
        setBusinessPlans([]); // Clear business plans when gateway is removed
        setShowRemoveModal(false);
        fetchGatewayStatus();
      } else {
        toast.error(response['data']['message'] || 'Failed to remove gateway');
      }
    } catch (error) {
      console.error('Remove error:', error);
      toast.error('Failed to remove payment gateway');
    }
    setLoading(false);
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e['target'];
    setFormData(prev => ({ ...prev, [name]: value.trim() }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  // Handle copy webhook
  const handleCopyWebhook = () => toast.success('Webhook URL copied to clipboard!');

  // Load agency plans
  const loadAgencyPlans = async () => {
    if (!workspaceId && !currentWorkspace?.['_id']) return;

    setLoadingAgencyPlans(true);
    try {
      const wsId = workspaceId || currentWorkspace?.['_id'];
      const response = await axios.get(`/payment/plans/agency/${wsId}`);

      console.log('Agency plans loaded:', {
        workspace: wsId,
        response: response['data']
      });

      if (response['data']['code'] === 200) {
        const plans = response['data']['body'] || [];
        setAgencyPlans(plans);
      }
    } catch (error) {
      console.error('Error loading agency plans:', error);
      toast.error('Failed to load agency plans');
    }
    setLoadingAgencyPlans(false);
  };

  // Load business plans for agency - only if Stripe is configured
  const loadBusinessPlans = async (skipStripeCheck = false) => {
    // Don't load business plans for non-agency views
    if (!isAgencyView) return;

    console.log('🔄 Attempting to load business plans...', {
      isAgencyView,
      workspaceId,
      currentWorkspaceId: currentWorkspace?.['_id'],
      gatewayStatus,
      skipStripeCheck
    });

    const wsId = workspaceId || currentWorkspace?.['_id'];

    console.log('Loading business plans for workspace:', {
      wsId,
      currentWorkspace: currentWorkspace?.['_id'],
      workspaceId
    });

    // Check if we have a valid workspace
    if (!wsId) {
      console.error('No workspace ID available');
      toast.error('No workspace ID available. Please check your agency configuration.');
      return;
    }

    // Only check Stripe configuration if not skipping (after successful config)
    if (!skipStripeCheck && (!gatewayStatus?.['isConfigured'] || gatewayStatus?.['validationStatus'] !== 'valid')) {
      console.log('⚠️ Stripe not configured or validated, skipping business plan load', gatewayStatus);
      setBusinessPlans([]);
      return;
    }

    setLoadingBusinessPlans(true);
    try {
      const response = await axios.get(`/payment/agencyPlans/${wsId}`);

      console.log('📋 Business plans API response:', response['data']);

      if (response['data']['code'] === 200) {
        const plans = response['data']['body'] || [];
        console.log(`✅ Loaded ${plans.length} business plans`);
        setBusinessPlans(plans);
      } else {
        console.error('Business plans API returned error:', response['data']['message']);
        toast.error('Failed to load business plans: ' + response['data']['message']);
      }
    } catch (error) {
      console.error('Error loading business plans for agency:', error);
      console.error('Error details:', {
        message: error['message'],
        status: error['response']?.['status'],
        statusText: error['response']?.['statusText'],
        responseData: error['response']?.['data']
      });

      // Only show error if Stripe is configured
      if (gatewayStatus?.['isConfigured']) {
        toast.error('Failed to load business plans. Please verify Stripe configuration.');
      }
    }
    setLoadingBusinessPlans(false);
  };

  // Handle plan actions
  const handleToggleStatus = async (planId) => {
    try {
      const response = await dispatch(togglePlanStatus(planId));
      if (response['data']['code'] === 200) {
        toast.success('Plan status updated successfully');
        loadPlans();
      }
    } catch (error) {
      toast.error('Failed to update plan status');
    }
  };

  const handleEditPlan = (plan) => {
    setSelectedPlan(plan);
    setShowEditModal(true);
  };

  const handleSavePlan = async (updatedPlan) => {
    try {
      const response = await axios.put(`/admin/plans/${updatedPlan['_id']}`, updatedPlan);
      if (response['data']['code'] === 200) {
        toast.success('Plan updated successfully');
        setShowEditModal(false);
        loadPlans();
      }
    } catch (error) {
      toast.error('Failed to update plan');
    }
  };

  // Handle plan assignment (for admin)
  const handleAssignPlan = (plan) => {
    setSelectedPlanForAssign(plan);
    setShowAssignModal(true);
  };

  const handleAssignPlanSubmit = async () => {
    if (!selectedWorkspaceForAssign || !selectedPlanForAssign) {
      toast.error('Please select a workspace');
      return;
    }

    setAssignmentLoading(true);
    try {
      const response = await axios.post('/admin/billing/plans/assign', {
        workspaceId: selectedWorkspaceForAssign['value'],
        planId: selectedPlanForAssign['_id'],
        reason: assignmentReason
      });

      if (response['data']['code'] === 200) {
        toast.success('Plan assigned successfully');
        setShowAssignModal(false);
        setSelectedWorkspaceForAssign(null);
        setAssignmentReason('');
      }
    } catch (error) {
      toast.error(error['response']?.['data']?.['message'] || 'Failed to assign plan');
    }
    setAssignmentLoading(false);
  };

  // Business plan pricing update
  const handleUpdateBusinessPlanPricing = async (planId, newPricing) => {
    try {
      const response = await axios.put(`/payment/agencyPlans/${planId}`, newPricing);
      if (response['data']['code'] === 200) {
        loadBusinessPlans();
        setShowPriceEditModal(false);
        toast.success('Plan pricing updated successfully');
      }
    } catch (error) {
      toast.error(error['response']?.['data']?.['message'] || 'Failed to update plan pricing');
    }
  }

  // Filter logic for plans
  const getFilteredPlans = () => {
    let plans = isAgencyView ? businessPlans : adminPlans;

    if (searchTerm) {
      plans = plans.filter(plan =>
        plan['name'].toLowerCase().includes(searchTerm.toLowerCase()) ||
        plan['displayName']?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (typeFilter) plans = plans.filter(plan => plan['type'] === typeFilter);
    if (statusFilter) plans = plans.filter(plan => plan['isActive'] === (statusFilter === 'active'));

    return plans;
  };

  const formatFeature = (key, value) => {
    const featureLabels = {
      users: 'Users',
      contacts: 'Contacts',
      monthlyCredits: 'Monthly Credits',
      callingMinutes: 'Calling Minutes',
      agentExecutions: 'Agent Executions',
      preBuiltAgents: 'Pre-built Agents',
      emails: 'Emails',
      subWorkspaces: 'Sub-Workspaces',
      supportType: 'Support'
    };

    const label = featureLabels[key] || key;

    if (value === -1 || value === 'unlimited') return `Unlimited ${label}`;
    if (typeof value === 'boolean') return value ? `✓ ${label}` : `✗ ${label}`;
    if (typeof value === 'number') return `${value.toLocaleString()} ${label}`;
    return `${value} ${label}`;
  };

  const renderPlanCard = (plan) => {
    const isPopular = plan['isPopular'];
    const isAgency = plan['creator'] == 'agency';
    return (
      <div className={`price-box ${isPopular ? 'border-primary shadow-lg' : ''} ${!plan['isActive'] ? 'opacity-75' : ''}`}>
        <div className="header-area">
          <h5>{plan['displayName'] || plan['name']}</h5>
          {plan['description'] && <p className="text-muted small mb-3">{plan['description']}</p>}
          {isPopular && (
            <div className='popular-box'>
              <svg width="65" height="17" viewBox="0 0 65 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="65" height="17" rx="8.5" fill="url(#paint0_linear_2466_3801)" />
                <path d="M22.792 12V5.295H25.096C25.534 5.295 25.918 5.382 26.248 5.556C26.578 5.724 26.833 5.964 27.013 6.276C27.199 6.582 27.292 6.939 27.292 7.347C27.292 7.755 27.199 8.112 27.013 8.418C26.833 8.724 26.578 8.964 26.248 9.138C25.924 9.306 25.54 9.39 25.096 9.39H23.512V12H22.792ZM23.512 8.715H25.132C25.42 8.715 25.672 8.661 25.888 8.553C26.104 8.439 26.272 8.28 26.392 8.076C26.512 7.866 26.572 7.62 26.572 7.338C26.572 7.056 26.512 6.813 26.392 6.609C26.272 6.405 26.104 6.249 25.888 6.141C25.672 6.027 25.42 5.97 25.132 5.97H23.512V8.715ZM30.5594 12.108C30.1034 12.108 29.6894 12 29.3174 11.784C28.9454 11.562 28.6484 11.262 28.4264 10.884C28.2104 10.5 28.1024 10.065 28.1024 9.579C28.1024 9.099 28.2104 8.67 28.4264 8.292C28.6424 7.914 28.9334 7.617 29.2994 7.401C29.6714 7.179 30.0914 7.068 30.5594 7.068C31.0274 7.068 31.4444 7.176 31.8104 7.392C32.1824 7.608 32.4734 7.905 32.6834 8.283C32.8994 8.661 33.0074 9.093 33.0074 9.579C33.0074 10.071 32.8964 10.509 32.6744 10.893C32.4524 11.271 32.1554 11.568 31.7834 11.784C31.4174 12 31.0094 12.108 30.5594 12.108ZM30.5594 11.433C30.8834 11.433 31.1744 11.352 31.4324 11.19C31.6964 11.028 31.9034 10.806 32.0534 10.524C32.2094 10.242 32.2874 9.927 32.2874 9.579C32.2874 9.231 32.2094 8.919 32.0534 8.643C31.9034 8.367 31.6964 8.148 31.4324 7.986C31.1744 7.824 30.8834 7.743 30.5594 7.743C30.2354 7.743 29.9414 7.824 29.6774 7.986C29.4194 8.148 29.2124 8.367 29.0564 8.643C28.9004 8.919 28.8224 9.231 28.8224 9.579C28.8224 9.927 28.9004 10.242 29.0564 10.524C29.2124 10.806 29.4194 11.028 29.6774 11.19C29.9414 11.352 30.2354 11.433 30.5594 11.433ZM34.1979 13.8V7.176H34.8729V8.256L34.7649 8.067C34.9569 7.761 35.2119 7.518 35.5299 7.338C35.8539 7.158 36.2229 7.068 36.6369 7.068C37.0929 7.068 37.4979 7.179 37.8519 7.401C38.2119 7.617 38.4939 7.914 38.6979 8.292C38.9079 8.67 39.0129 9.102 39.0129 9.588C39.0129 10.068 38.9079 10.5 38.6979 10.884C38.4939 11.262 38.2119 11.562 37.8519 11.784C37.4979 12 37.0929 12.108 36.6369 12.108C36.2229 12.108 35.8479 12.015 35.5119 11.829C35.1819 11.637 34.9329 11.367 34.7649 11.019L34.8729 10.92V13.8H34.1979ZM36.6009 11.433C36.9249 11.433 37.2129 11.352 37.4649 11.19C37.7229 11.028 37.9239 10.809 38.0679 10.533C38.2179 10.251 38.2929 9.936 38.2929 9.588C38.2929 9.234 38.2179 8.919 38.0679 8.643C37.9239 8.367 37.7229 8.148 37.4649 7.986C37.2129 7.824 36.9249 7.743 36.6009 7.743C36.2769 7.743 35.9829 7.824 35.7189 7.986C35.4609 8.148 35.2539 8.37 35.0979 8.652C34.9479 8.928 34.8729 9.24 34.8729 9.588C34.8729 9.936 34.9479 10.251 35.0979 10.533C35.2539 10.809 35.4609 11.028 35.7189 11.19C35.9829 11.352 36.2769 11.433 36.6009 11.433ZM41.9108 12.108C41.5748 12.108 41.2688 12.03 40.9928 11.874C40.7168 11.712 40.4978 11.49 40.3358 11.208C40.1798 10.92 40.1018 10.59 40.1018 10.218V7.176H40.7768V10.137C40.7768 10.395 40.8278 10.623 40.9298 10.821C41.0378 11.013 41.1848 11.163 41.3708 11.271C41.5628 11.379 41.7818 11.433 42.0278 11.433C42.2738 11.433 42.4928 11.379 42.6848 11.271C42.8768 11.157 43.0268 10.998 43.1348 10.794C43.2428 10.584 43.2968 10.335 43.2968 10.047V7.176H43.9718V12H43.2968V11.064L43.4498 11.1C43.3358 11.412 43.1408 11.658 42.8648 11.838C42.5888 12.018 42.2708 12.108 41.9108 12.108ZM45.36 12V5.187H46.035V12H45.36ZM48.8116 12.108C48.5056 12.108 48.2326 12.051 47.9926 11.937C47.7526 11.817 47.5636 11.655 47.4256 11.451C47.2876 11.247 47.2186 11.013 47.2186 10.749C47.2186 10.497 47.2726 10.269 47.3806 10.065C47.4886 9.855 47.6566 9.678 47.8846 9.534C48.1126 9.39 48.4036 9.288 48.7576 9.228L50.6476 8.913V9.525L48.9196 9.813C48.5716 9.873 48.3196 9.984 48.1636 10.146C48.0136 10.308 47.9386 10.5 47.9386 10.722C47.9386 10.938 48.0226 11.121 48.1906 11.271C48.3646 11.421 48.5866 11.496 48.8566 11.496C49.1866 11.496 49.4746 11.427 49.7206 11.289C49.9666 11.145 50.1586 10.953 50.2966 10.713C50.4346 10.473 50.5036 10.206 50.5036 9.912V8.688C50.5036 8.4 50.3986 8.166 50.1886 7.986C49.9786 7.806 49.7056 7.716 49.3696 7.716C49.0756 7.716 48.8176 7.791 48.5956 7.941C48.3736 8.085 48.2086 8.274 48.1006 8.508L47.4886 8.175C47.5786 7.971 47.7196 7.785 47.9116 7.617C48.1096 7.449 48.3346 7.317 48.5866 7.221C48.8386 7.119 49.0996 7.068 49.3696 7.068C49.7236 7.068 50.0356 7.137 50.3056 7.275C50.5816 7.413 50.7946 7.605 50.9446 7.851C51.1006 8.091 51.1786 8.37 51.1786 8.688V12H50.5036V11.037L50.6026 11.136C50.5186 11.316 50.3866 11.481 50.2066 11.631C50.0326 11.775 49.8256 11.892 49.5856 11.982C49.3516 12.066 49.0936 12.108 48.8116 12.108ZM52.567 12V7.176H53.242V7.968L53.152 7.851C53.266 7.623 53.437 7.443 53.665 7.311C53.899 7.179 54.181 7.113 54.511 7.113H54.817V7.788H54.394C54.046 7.788 53.767 7.896 53.557 8.112C53.347 8.328 53.242 8.634 53.242 9.03V12H52.567Z" fill="white" />
                <path d="M16.4068 7.58334C16.4068 7.58334 16.3381 8.04168 14.9172 9.87501C13.5835 11.6029 15.5131 12.9183 15.7422 13.0696C15.756 13.0788 15.7697 13.0788 15.7881 13.0696C16.0997 12.8771 19.5693 10.645 16.4068 7.58334Z" fill="white" />
                <path d="M15.3066 6.57041C15.3066 5.51625 14.8941 4.55375 14.4816 4.00375C14.3441 3.86625 14.1149 3.91208 14.0691 4.09541C13.8857 4.78291 13.3357 6.24958 12.0066 7.99125C10.3107 10.1912 11.8691 12.5746 13.4732 13.0329C14.3441 13.2621 13.2441 12.5746 13.1066 11.1537C12.9691 9.36625 15.3066 8.03708 15.3066 6.57041Z" fill="white" />
                <defs>
                  <linearGradient id="paint0_linear_2466_3801" x1="0" y1="8.5" x2="65" y2="8.5" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#493289" />
                    <stop offset="1" stop-color="#CE7DFA" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          )}
          <h2>${plan['price']} <span>/{plan['interval'] || 'month'}</span></h2>
          {plan['type'] === 'credits' && (
            <Badge color="warning" className="mt-2">One-time Purchase</Badge>
          )}
        </div>
        <div className='feature-list'>
          <ul>
            {plan['featuresWithIcons']?.length > 0 ? (
              plan['featuresWithIcons'].map((feature, idx) => (
                <li key={idx} className="mb-2">
                  {/* {feature['icon'] && <i className={`${feature['icon']} mr-2 text-primary`}></i>} */}
                  <span className={feature['highlighted'] ? 'font-weight-bold' : ''}>
                    {feature['text']}
                  </span>
                </li>
              ))
            ) : (
              Object.entries(plan['features'] || {}).map(([key, value]) => (
                <li key={key} className="mb-2">
                  {/* <i className="fa fa-check mr-2 text-success"></i> */}
                  {formatFeature(key, value)}
                </li>
              ))
            )}
            {plan['monthlyCredits'] && (
              <li className="mb-2">
                {/* <i className="fa fa-coins mr-2 text-warning"></i> */}
                {plan['monthlyCredits'].toLocaleString()} Monthly Credits
              </li>
            )}
          </ul>
        </div>
        {/* Plan Metadata */}
        <div className="planbox-admin">
          <div>Type: {plan['type']}</div>
          <div>Plan ID: {plan['_id']}</div>
          {plan['stripeProductId'] && <div>Stripe Product: {plan['stripeProductId']}</div>}
          {plan['stripePriceId'] && <div>Stripe Price: {plan['stripePriceId']}</div>}
          {isAgency && plan['basePrice'] && (
            <div>Base Price: ${plan['basePrice']}</div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-4 ">
          {isAgency ? (
            <button
              size="sm"
              className="btn-style-border btn-full-width"
              onClick={() => {
                setShowPriceEditModal(true);
                setSelectedPlanForPriceEdit(plan);
              }}
            >
              <i className="fa fa-edit mr-2"></i>
              Customize Pricing
            </button>
          ) : (
            <>
              <ButtonGroup size="sm" className="w-100">
                <button
                  onClick={() => handleToggleStatus(plan['_id'])}
                  color={plan['isActive'] ? 'warning' : 'success'}
                  className={plan['isActive'] ? 'btn-style-border' : 'btn-style-new'}
                >
                  <i className={`fa fa-${plan['isActive'] ? 'pause' : 'play'} mr-1`}></i>
                  {plan['isActive'] ? 'Deactivate' : 'Activate'}
                </button>
                <button className="btn-style-new ml-3" onClick={() => handleEditPlan(plan)}>
                  <i className="fa fa-edit mr-1"></i> Edit
                </button>
              </ButtonGroup>
              {accessType === 'admin' && (
                <button
                  block
                  size="sm"
                  className="btn-style-new mt-2 btn-full-width"
                  onClick={() => handleAssignPlan(plan)}
                >
                  <i className="fa fa-user-plus mr-1"></i> Assign to Workspace
                </button>
              )}
            </>
          )}
        </div>
      </div>
    )
  }

  const renderTableView = () => {
    const filteredPlans = getFilteredPlans();
    return (
      <Table responsive hover>
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Price</th>
            <th>Status</th>
            <th>Features</th>
            <th className="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredPlans.map(plan => (
            <tr key={plan['_id']} className={!plan['isActive'] ? 'table-secondary' : ''}>
              <td>
                <div>
                  <strong>{plan['displayName'] || plan['name']}</strong>
                  {plan['isPopular'] && <Badge color="primary" className="ml-2">Popular</Badge>}
                  {plan['description'] && <div className="small text-muted">{plan['description']}</div>}
                </div>
              </td>
              <td>{plan['type']}</td>
              <td>${plan['price']}/{plan['interval'] || 'month'}</td>
              <td>
                <Badge color={plan['isActive'] ? 'success' : 'danger'}>
                  {plan['isActive'] ? 'Active' : 'Inactive'}
                </Badge>
              </td>
              <td>
                <small>
                  {Object.entries(plan['features'] || {})
                    .slice(0, 3)
                    .map(([key, value]) => formatFeature(key, value))
                    .join(', ')}
                  ...
                </small>
              </td>
              <td className="text-center">
                <div className='action-box actionbox-plan'>
                  {plan['isAgencyBusinessPlan'] ? (
                    <button
                      className='action-btn' title="Edit Pricing"
                      size="sm"
                      onClick={() => {
                        setSelectedPlanForPriceEdit(plan);
                        setShowPriceEditModal(true);
                      }}
                    >
                      <i className="icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.1" cx="12" cy="12" r="12" fill="#A46497"></circle><path d="M13.5 17.375H10.5C7.785 17.375 6.625 16.215 6.625 13.5V10.5C6.625 7.785 7.785 6.625 10.5 6.625H11.5C11.705 6.625 11.875 6.795 11.875 7C11.875 7.205 11.705 7.375 11.5 7.375H10.5C8.195 7.375 7.375 8.195 7.375 10.5V13.5C7.375 15.805 8.195 16.625 10.5 16.625H13.5C15.805 16.625 16.625 15.805 16.625 13.5V12.5C16.625 12.295 16.795 12.125 17 12.125C17.205 12.125 17.375 12.295 17.375 12.5V13.5C17.375 16.215 16.215 17.375 13.5 17.375Z" fill="#A46497"></path><path d="M10.2497 14.845C9.94467 14.845 9.66467 14.735 9.45967 14.535C9.21467 14.29 9.10967 13.935 9.16467 13.56L9.37967 12.055C9.41967 11.765 9.60967 11.39 9.81467 11.185L13.7547 7.245C14.7497 6.25 15.7597 6.25 16.7547 7.245C17.2997 7.79 17.5447 8.345 17.4947 8.9C17.4497 9.35 17.2097 9.79 16.7547 10.24L12.8147 14.18C12.6097 14.385 12.2347 14.575 11.9447 14.615L10.4397 14.83C10.3747 14.845 10.3097 14.845 10.2497 14.845ZM14.2847 7.775L10.3447 11.715C10.2497 11.81 10.1397 12.03 10.1197 12.16L9.90467 13.665C9.88467 13.81 9.91467 13.93 9.98967 14.005C10.0647 14.08 10.1847 14.11 10.3297 14.09L11.8347 13.875C11.9647 13.855 12.1897 13.745 12.2797 13.65L16.2197 9.71C16.5447 9.385 16.7147 9.095 16.7397 8.825C16.7697 8.5 16.5997 8.155 16.2197 7.77C15.4197 6.97 14.8697 7.195 14.2847 7.775Z" fill="#A46497"></path><path d="M15.9248 10.915C15.8898 10.915 15.8548 10.91 15.8248 10.9C14.5098 10.53 13.4648 9.485 13.0948 8.17C13.0398 7.97 13.1548 7.765 13.3548 7.705C13.5548 7.65 13.7598 7.765 13.8148 7.965C14.1148 9.03 14.9598 9.875 16.0248 10.175C16.2248 10.23 16.3398 10.44 16.2848 10.64C16.2398 10.81 16.0898 10.915 15.9248 10.915Z" fill="#A46497"></path></svg></i>
                    </button>
                  ) : isAgencyView ? (
                    <span className="text-muted">-</span>
                  ) : (
                    <UncontrolledDropdown>
                      <DropdownToggle color="secondary" size="sm">
                        <i className="fa fa-ellipsis-h"></i>
                      </DropdownToggle>
                      <DropdownMenu right>
                        <DropdownItem onClick={() => handleEditPlan(plan)}>
                          <i className="fa fa-edit mr-2"></i> Edit
                        </DropdownItem>
                        <DropdownItem onClick={() => handleToggleStatus(plan['_id'])}>
                          <i className={`fa fa-${plan['isActive'] ? 'pause' : 'play'} mr-2`}></i>
                          {plan['isActive'] ? 'Deactivate' : 'Activate'}
                        </DropdownItem>
                        {accessType === 'admin' && (
                          <DropdownItem onClick={() => handleAssignPlan(plan)}>
                            <i className="fa fa-user-plus mr-2"></i> Assign
                          </DropdownItem>
                        )}
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  )}
                </div>

              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  // Loading state - show loader while fetching initial gateway status
  if (isAgencyView && gatewayLoading) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center py-5">
        <div className="spinner-border text-primary mb-3" role="status">
          <span className="sr-only">Loading...</span>
        </div>
        <p className="text-muted mb-0">Loading payment gateway status...</p>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <Container fluid className="px-0">
      {/* Payment Gateway Section - Only show for Agency users AND when not configured */}
      {isAgencyView && !gatewayStatus?.['isConfigured'] && (
        <Row className="justify-content-center mb-4">
          <Col lg="8" md="10">
            <Card>
              <CardHeader>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <h4><i className="fas fa-credit-card mr-2"></i> Stripe Payment Gateway</h4>
                    {/* <CardTitle tag="h4" className="mb-0">
                      <i className="fas fa-credit-card mr-2"></i>
                      Stripe Payment Gateway
                    </CardTitle> */}
                  </div>
                </div>
              </CardHeader>

              <CardBody>
                {/* Always show the configuration form when the card is displayed */}
                <>
                  <Alert color="warning" className="mb-4">
                    <strong>Stripe Configuration Required</strong>
                    <div className="mt-1">
                      Configure your Stripe account to activate and manage subscription plans. You can still create plans as drafts.
                    </div>
                  </Alert>

                  <Form onSubmit={handleSubmit}>
                    {errors['environment'] && <Alert color="danger">{errors['environment']}</Alert>}

                    <FormGroup className="stripe-group">
                      <Label for="secretKey">
                        Stripe Secret Key <span className="text-danger">*</span>
                      </Label>
                      <InputGroup>
                        <Input
                          type={showSecretKey ? 'text' : 'password'}
                          name="secretKey"
                          id="secretKey"
                          placeholder="sk_test_... or sk_live_..."
                          value={formData['secretKey']}
                          onChange={handleInputChange}
                          invalid={!!errors['secretKey']}
                        />
                        <InputGroupText className="cursor-pointer" onClick={() => setShowSecretKey(!showSecretKey)}>
                          <i className={`fas fa-${showSecretKey ? 'eye-slash' : 'eye'}`}></i>
                        </InputGroupText>
                      </InputGroup>
                      {errors['secretKey'] && (
                        <div className="invalid-feedback d-block">{errors['secretKey']}</div>
                      )}
                      <small className="form-text text-muted">
                        Your Stripe secret key (starts with sk_test_ or sk_live_)
                        <strong className="text-warning"> - Keep this secure!</strong>
                      </small>
                    </FormGroup>

                    <Alert color="info" className="mt-3">
                      <i className="fas fa-info-circle mr-2"></i>
                      <strong>Automatic Setup:</strong> We'll automatically create the webhook endpoint and sync your business plans to your Stripe account.
                    </Alert>

                    <button color="primary" type="submit" disabled={validating} className="mt-3 btn-style-new">
                      {validating ? (
                        <>
                          <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                          Connecting & Setting Up...
                        </>
                      ) : <> <i className="fas fa-check mr-2"></i> Connect Stripe </>
                      }
                    </button>
                  </Form>

                  <Alert color="secondary" className="mt-4">
                    <h6>What happens next:</h6>
                    <ol className="pl-3 mb-0">
                      <li>We'll validate your Stripe account credentials</li>
                      <li>Automatically create a webhook endpoint in your Stripe dashboard</li>
                      <li>Sync all business plans as products in your Stripe account</li>
                      <li>You'll be ready to accept payments immediately!</li>
                    </ol>
                  </Alert>
                </>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Plans Section */}
      <div className="">
        <div className="d-flex justify-content-between align-items-center mb-4">

          {/* Show Stripe status badge for agency users when configured */}
          {isAgencyView && gatewayStatus?.['isConfigured'] && (
            <>
              <h3 className="mb-0">
                <i className="fas fa-cube mr-2"></i>
                Subscription Plans
              </h3>
              <div className="d-flex align-items-center gap-2">
                <Badge color="success" pill>
                  <i className="fas fa-check-circle mr-1"></i>
                  Stripe Connected
                </Badge>
                {gatewayStatus['liveMode'] ?
                  <Badge color="danger" pill>LIVE MODE</Badge>
                  : <Badge color="warning" pill>TEST MODE</Badge>}
                <button className="btn-style-new" size="sm" color="link" onClick={() => setIsGatewayCollapsed(!isGatewayCollapsed)}>
                  <i className={`fas fa-chevron-${isGatewayCollapsed ? 'down' : 'up'}`}></i>
                </button>
              </div>
            </>
          )}
        </div>

        {/* Collapsible Stripe Configuration Details - Only for agency when configured */}
        {isAgencyView && gatewayStatus?.['isConfigured'] && (
          <Collapse isOpen={!isGatewayCollapsed}>
            <Card className="mb-4">
              <CardBody>
                <Row className="mb-3">
                  <Col md={6}>
                    <p className="mb-1"> <strong>Account:</strong> {gatewayStatus['accountName'] || 'Stripe Account'} </p>
                    <p className="mb-1"> <strong>Account ID:</strong> {gatewayStatus['accountId']} </p>
                    <p className="mb-1"> <strong>Email:</strong> {gatewayStatus['accountEmail']} </p>
                  </Col>
                  <Col md={6}>
                    <p className="mb-1">
                      <strong>Configured:</strong> {new Date(gatewayStatus['configuredAt']).toLocaleDateString()}
                    </p>
                    <p className="mb-1">
                      <strong>Last Validated:</strong> {gatewayStatus['lastValidated'] ? new Date(gatewayStatus['lastValidated']).toLocaleDateString() : 'Never'}
                    </p>
                    <p className="mb-1">
                      <strong>Capabilities:</strong> {gatewayStatus['capabilities']?.join(', ') || 'None'}
                    </p>
                  </Col>
                </Row>
                <div className="mt-3">
                  <button color="info" size="sm" onClick={handleValidate} disabled={validating} className="mr-2 btn-style-new">
                    {validating ? <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                      : <i className="fas fa-check mr-1"></i>}
                    Validate Connection
                  </button>
                  <button color="danger" className="btn-style-border" size="sm" onClick={() => setShowRemoveModal(true)}>
                    <i className="fas fa-trash mr-1"></i>
                    Remove Configuration
                  </button>
                </div>

                {/* Webhook Configuration Info */}
                {gatewayStatus?.['webhookUrl'] && (
                  <Alert color="info" className="mt-4">
                    <h6>Webhook Configuration</h6>
                    <InputGroup className="mb-2 webhook-group">
                      <Input readOnly type="text" id="webhook-url" value={gatewayStatus['webhookUrl']} />
                      <CopyToClipboard text={gatewayStatus['webhookUrl']} onCopy={handleCopyWebhook}>
                        <button className="btn-style-new" color="secondary">
                          <i className="fas fa-copy mr-1"></i>
                          Copy
                        </button>
                      </CopyToClipboard>
                    </InputGroup>
                    <UncontrolledTooltip placement="top" target="webhook-url">
                      Add this URL to your Stripe webhook endpoints
                    </UncontrolledTooltip>
                  </Alert>
                )}
              </CardBody>
            </Card>
          </Collapse>
        )}

        {/* Plan Assignment Section removed for agency view - keeping only for admin users */}

        {/* Plan Assignment for Admin Users */}
        {accessType === 'admin' && (
          <Alert color="info" className="mb-4">
            <i className="fa fa-info-circle mr-2"></i>
            You can assign plans directly to workspaces for special cases or testing purposes.
          </Alert>
        )}

        {/* Search and Filter Bar - Always show when there are plans to display */}
        <div className='content-dashboard'>
          <div className="table-container filters-table">
            {(!isAgencyView || gatewayStatus?.['isConfigured']) && (isAgencyView ? businessPlans.length > 0 : adminPlans.length > 0) && (
              <>
                {/* Filters */}
                <div className='header-table-filters advanced-table-filters d-flex flex-column flex-md-row flex-wrap align-items-start align-items-md-center justify-content-between gap-3'>
                  <div className='left-area filters-left-area d-flex flex-column flex-md-row flex-wrap gap-3 w-100 w-md-auto'>
                    <div className='filter-box flex-grow-1 flex-md-grow-0' style={{ minWidth: '200px' }}>
                      <div className='searchbox'>
                        <TextField
                          fullWidth
                          hiddenLabel
                          size="small"
                          name='search'
                          variant="standard"
                          value={searchTerm}
                          placeholder="Search plans..."
                          onChange={(e) => setSearchTerm(e['target']['value'])}
                        />
                        <i className='icon'>
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5.82319 10.6464C3.16455 10.6464 1 8.48183 1 5.82319C1 3.16455 3.16455 1 5.82319 1C8.48183 1 10.6464 3.16455 10.6464 5.82319C10.6464 8.48183 8.48183 10.6464 5.82319 10.6464ZM5.82319 1.70583C3.55041 1.70583 1.70583 3.55511 1.70583 5.82319C1.70583 8.09127 3.55041 9.94055 5.82319 9.94055C8.09597 9.94055 9.94055 8.09127 9.94055 5.82319C9.94055 3.55511 8.09597 1.70583 5.82319 1.70583Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                            <path d="M13.3182 13.7068C13.2245 13.7069 13.1307 13.6725 13.0566 13.5985L9.10425 9.63167C8.96097 9.48877 8.96065 9.25192 9.10355 9.10864C9.24646 8.96536 9.4833 8.96505 9.62658 9.10795L13.5789 13.0748C13.7222 13.2177 13.7225 13.4546 13.5796 13.5979C13.5057 13.672 13.412 13.7066 13.3182 13.7068Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                          </svg>
                        </i>
                      </div>
                    </div>
                    <div className='filter-box d-flex flex-column flex-sm-row gap-3 flex-grow-1 flex-md-grow-0' style={{ minWidth: '200px' }}>

                      {/* Status Filter */}
                      <Input type="select" value={typeFilter} onChange={(e) => setTypeFilter(e['target']['value'])} className="w-100 w-sm-auto">
                        <option value="">All Types</option>
                        <option value="subscription">Subscription</option>
                        <option value="credits">Credits</option>
                      </Input>

                      <Input type="select" value={statusFilter} onChange={(e) => setStatusFilter(e['target']['value'])} className="w-100 w-sm-auto">
                        <option value="">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </Input>


                    </div>
                  </div>
                  <div className='right-deals-filtes filters-right-area d-flex justify-content-start justify-content-md-end w-100 w-md-auto'>
                    <div className='filter-table-select'>
                      <button className={`filters-button ${viewMode === 'card' ? 'primary' : 'active'}`} onClick={() => setViewMode('card')}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M4.82449 13.2707H2.46783C1.29533 13.2707 0.729492 12.7282 0.729492 11.6082V2.3915C0.729492 1.2715 1.30116 0.729004 2.46783 0.729004H4.82449C5.99699 0.729004 6.56283 1.2715 6.56283 2.3915V11.6082C6.56283 12.7282 5.99116 13.2707 4.82449 13.2707ZM2.46783 1.604C1.72699 1.604 1.60449 1.80234 1.60449 2.3915V11.6082C1.60449 12.1973 1.72699 12.3957 2.46783 12.3957H4.82449C5.56533 12.3957 5.68783 12.1973 5.68783 11.6082V2.3915C5.68783 1.80234 5.56533 1.604 4.82449 1.604H2.46783V1.604Z" fill="currentColor" />
                          <path d="M11.5325 9.18734H9.17583C8.00333 9.18734 7.4375 8.64484 7.4375 7.52484V2.3915C7.4375 1.2715 8.00917 0.729004 9.17583 0.729004H11.5325C12.705 0.729004 13.2708 1.2715 13.2708 2.3915V7.52484C13.2708 8.64484 12.6992 9.18734 11.5325 9.18734ZM9.17583 1.604C8.435 1.604 8.3125 1.80234 8.3125 2.3915V7.52484C8.3125 8.114 8.435 8.31234 9.17583 8.31234H11.5325C12.2733 8.31234 12.3958 8.114 12.3958 7.52484V2.3915C12.3958 1.80234 12.2733 1.604 11.5325 1.604H9.17583V1.604Z" fill="currentColor" />
                        </svg>
                      </button>
                      <button className={`filters-button ${viewMode === 'table' ? 'primary' : 'active'}`} onClick={() => setViewMode('table')}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M11.6087 13.2708H2.39199C1.27199 13.2708 0.729492 12.6992 0.729492 11.5325V9.17583C0.729492 8.00333 1.27199 7.4375 2.39199 7.4375H11.6087C12.7287 7.4375 13.2712 8.00917 13.2712 9.17583V11.5325C13.2712 12.6992 12.7287 13.2708 11.6087 13.2708ZM2.39199 8.3125C1.80283 8.3125 1.60449 8.435 1.60449 9.17583V11.5325C1.60449 12.2733 1.80283 12.3958 2.39199 12.3958H11.6087C12.1978 12.3958 12.3962 12.2733 12.3962 11.5325V9.17583C12.3962 8.435 12.1978 8.3125 11.6087 8.3125H2.39199Z" fill="currentColor" />
                          <path d="M11.6087 6.56234H2.39199C1.27199 6.56234 0.729492 5.99067 0.729492 4.824V2.46734C0.729492 1.29484 1.27199 0.729004 2.39199 0.729004H11.6087C12.7287 0.729004 13.2712 1.30067 13.2712 2.46734V4.824C13.2712 5.99067 12.7287 6.56234 11.6087 6.56234ZM2.39199 1.604C1.80283 1.604 1.60449 1.7265 1.60449 2.46734V4.824C1.60449 5.56484 1.80283 5.68734 2.39199 5.68734H11.6087C12.1978 5.68734 12.3962 5.56484 12.3962 4.824V2.46734C12.3962 1.7265 12.1978 1.604 11.6087 1.604H2.39199V1.604Z" fill="currentColor" />
                        </svg>
                      </button>
                    </div>

                  </div>
                </div>
              </>
            )}
            <div className='table-area'>
              {/* Plans Display */}
              {(!isAgencyView || gatewayStatus?.['isConfigured']) && (viewMode === 'card' ? (
                  <Row>
                    {loadingBusinessPlans || adminPlansLoading ?
                      <Col className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="sr-only">Loading plans...</span>
                        </div>
                      </Col>
                      : getFilteredPlans().length > 0
                        ? <div className='prices-boxes-area'>
                          <Masonry
                            breakpointCols={breakpoints}
                            className="masonry-grid"
                            columnClassName="masonry-column"
                          >
                            {getFilteredPlans().map(renderPlanCard)}
                          </Masonry>
                        </div>
                        :
                        <Col className="text-center py-5">
                          <h4 className="text-muted no-plans-message">No plans found</h4>
                        </Col>
                    }
                  </Row>
                ) : renderTableView()
              )}
            </div>

          </div>
        </div>
      </div>

      {/* Edit Plan Modal */}
      {showEditModal && selectedPlan && (
        <PlanEditModal
          plan={selectedPlan}
          isOpen={showEditModal}
          onSave={handleSavePlan}
          toggle={() => setShowEditModal(false)}
        />
      )}

      {/* Price Edit Modal for Business Plans */}
      <Modal isOpen={showPriceEditModal} toggle={() => setShowPriceEditModal(false)} className="main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal">
        <ModalHeader toggle={() => setShowPriceEditModal(false)}>
          <h3>Customize Plan Pricing</h3>
        </ModalHeader>
        <ModalBody>
          <div className='content mt-4'>
            {selectedPlanForPriceEdit && (
              <Form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e['target']);
                handleUpdateBusinessPlanPricing(selectedPlanForPriceEdit['_id'], {
                  displayName: formData.get('displayName'),
                  customPrice: parseFloat(formData.get('customPrice')),
                  customMonthlyCredits: parseInt(formData.get('customMonthlyCredits'))
                });
              }}>
                <h5>{selectedPlanForPriceEdit['displayName']}</h5>
                <p className="text-muted">Customize pricing for your agency</p>

                <FormGroup>
                  <Label for="displayName">Plan Display Name</Label>
                  <Input
                    type="text"
                    name="displayName"
                    id="displayName"
                    defaultValue={selectedPlanForPriceEdit['displayName']}
                    placeholder="Enter plan display name"
                    required
                  />
                  <small className="text-muted">
                    This is the name your customers will see
                  </small>
                </FormGroup>

                <FormGroup>
                  <Label>Base Price</Label>
                  <Input
                    type="text"
                    value={`$${selectedPlanForPriceEdit['basePrice'] || selectedPlanForPriceEdit['price']}`}
                    disabled
                  />
                </FormGroup>

                <FormGroup>
                  <Label for="customPrice">Your Price</Label>
                  <InputGroup>
                    <InputGroupText>$</InputGroupText>
                    <Input
                      type="number"
                      name="customPrice"
                      id="customPrice"
                      min={selectedPlanForPriceEdit['basePrice'] || selectedPlanForPriceEdit['price']}
                      step="0.01"
                      defaultValue={selectedPlanForPriceEdit['price']}
                      required
                    />
                  </InputGroup>
                  <small className="text-muted">
                    Minimum: ${selectedPlanForPriceEdit['basePrice'] || selectedPlanForPriceEdit['price']}
                  </small>
                </FormGroup>

                <FormGroup>
                  <Label for="customMonthlyCredits">Monthly Credits</Label>
                  <Input
                    type="number"
                    name="customMonthlyCredits"
                    id="customMonthlyCredits"
                    min="0"
                    defaultValue={selectedPlanForPriceEdit['monthlyCredits']}
                  />
                </FormGroup>

                <div className="d-flex justify-content-end">
                  <button type="button" className="mr-2 btn-style-border" onClick={() => setShowPriceEditModal(false)}>
                    Cancel
                  </button>
                  <button className="btn-style-new" type="submit">
                    Save Changes
                  </button>
                </div>
              </Form>
            )}
          </div>
        </ModalBody>
      </Modal>

      {/* Remove Gateway Modal */}
      <Modal isOpen={showRemoveModal} toggle={() => setShowRemoveModal(false)} className="main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal">
        <ModalHeader toggle={() => setShowRemoveModal(false)}>
          <h3>Remove Payment Gateway</h3>
        </ModalHeader>
        <ModalBody>
          <div className='content mt-4'>
            <Alert color="danger">
              <h5 className="warning-text"><i className="fa fa-exclamation-triangle mr-2"></i> <strong>Warning! </strong> Removing the payment gateway will:</h5>
              <ul className="mt-2 mb-0 warning-text warning-list">
                <li>Disconnect your Stripe account</li>
                <li>Prevent new subscriptions from being created</li>
                <li>Disable payment processing for sub-workspaces</li>
                <li>Remove webhook configuration from Stripe</li>
              </ul>
            </Alert>
            <p>Are you sure you want to remove the payment gateway configuration?</p>
          </div>
        </ModalBody>
        <ModalFooter>
          <button className="btn-style-border" onClick={() => setShowRemoveModal(false)}>
            Cancel
          </button>
          <button className="btn-style-new" onClick={handleRemoveGateway} disabled={loading}>
            {loading ? (
              <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
            ) : (
              <i className="fa fa-trash mr-2"></i>
            )}
            Remove Gateway
          </button>
        </ModalFooter>
      </Modal>

      {/* Assign Plan Modal (Admin Only) */}
      <Modal isOpen={showAssignModal} toggle={() => setShowAssignModal(false)} className="main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal">
        <ModalHeader toggle={() => setShowAssignModal(false)}>
          <h3>Assign Plan to Workspace</h3>
        </ModalHeader>
        <ModalBody>
          {selectedPlanForAssign && (
            <div className='content mt-4'>
              <h5>Plan: {selectedPlanForAssign['displayName'] || selectedPlanForAssign['name']}</h5>
              <p className="text-muted">Price: ${selectedPlanForAssign['price']}/{selectedPlanForAssign['interval'] || 'month'}</p>

              <FormGroup>
                <Label for="workspace">Select Workspace</Label>
                <Select
                  id="workspace"
                  value={selectedWorkspaceForAssign}
                  onChange={setSelectedWorkspaceForAssign}
                  options={workspaces.map(ws => ({ value: ws['_id'], label: `${ws['name']} (${ws['ownerEmail']})` }))}
                  placeholder="Choose a workspace..."
                />
              </FormGroup>

              <FormGroup>
                <Label for="reason">Reason for Assignment</Label>
                <Input
                  type="textarea"
                  id="reason"
                  value={assignmentReason}
                  onChange={(e) => setAssignmentReason(e['target']['value'])}
                  placeholder="Optional: Provide a reason for this assignment..."
                  rows={3}
                />
              </FormGroup>

              <Alert color="info">
                <i className="fa fa-info-circle mr-2"></i>
                This will override the workspace's current subscription and assign this plan directly.
              </Alert>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <button className="btn-style-border" color="secondary" onClick={() => setShowAssignModal(false)}>
            Cancel
          </button>
          <button
            className="btn-style-new"
            onClick={handleAssignPlanSubmit}
            disabled={!selectedWorkspaceForAssign || assignmentLoading}
          >
            {assignmentLoading ? (
              <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
            ) : (
              <i className="fa fa-check mr-2"></i>
            )}
            Assign Plan
          </button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default Plans;